import { AddressDetails } from '@vend/business-components/dist/components/AddressForm/types'
import { validatePostCode } from '@vend/utilities'
import { isEmpty } from 'lodash'
import {
  AMERICAN_STATES,
  CANADA_COUNTRY_CODE,
  CANADIAN_PROVINCES,
  US_COUNTRY_CODE,
  VD_COUNTRY_CODES,
} from '../../../../../vend-ui/constants'
import { getFeatureFromCache } from '../../../../../util/feature-cache'
import { FEATURE_ENABLE_LRX_33701_STATE_VALIDATION } from '../../../../../constants'

interface CountryCodesMap {
  [countryCode: string]: { code: string; name: string }
}

export interface AddressFieldValidationResult {
  isValid: boolean
  errorMessage: string
  _context: string
}

const countryCodesMap: CountryCodesMap = VD_COUNTRY_CODES.reduce(
  (map, country) => {
    map[country.code] = country
    return map
  },
  {} as CountryCodesMap
)
const americanStatesMap = AMERICAN_STATES.map(state => {
  return { code: state.code.toUpperCase(), name: state.name.toUpperCase() }
})
const canadianProvincesMap = CANADIAN_PROVINCES.map(province => {
  return {
    code: province.code.toUpperCase(),
    name: province.name.toUpperCase(),
  }
})
const findAmericanState = (state: string) => {
  const stateUpper = state.toUpperCase()
  return americanStatesMap.find(
    usState => usState.code === stateUpper || usState.name === stateUpper
  )
}
const findCanadianProvince = (province: string) => {
  const provinceUpper = province.toUpperCase()
  return canadianProvincesMap.find(
    caProvince =>
      caProvince.code === provinceUpper || caProvince.name === provinceUpper
  )
}

const findStateOrProvince = (addressState: string, countryId: string) => {
  const countryIdUpper = countryId.toUpperCase()
  if (countryIdUpper === US_COUNTRY_CODE) {
    return findAmericanState(addressState)
  }
  if (countryIdUpper === CANADA_COUNTRY_CODE) {
    return findCanadianProvince(addressState)
  }
}

const MAX_ADDRESS_LENGTH = 50
const MAX_CITY_LENGTH = 28
const MAX_STATE_LENGTH = 35
const COUNTRIES_WITH_STATES = [US_COUNTRY_CODE, CANADA_COUNTRY_CODE]
const DEFAULT_ERROR_RESULT: AddressFieldValidationResult = {
  isValid: false,
  errorMessage: '',
  _context: 'Error text',
}
const DEFAULT_SUCCESS_RESULT: AddressFieldValidationResult = {
  isValid: true,
  errorMessage: '',
  _context: '',
}

const isStateRequired = (country_id: string): boolean => {
  return COUNTRIES_WITH_STATES.includes(country_id.toUpperCase())
}

const isStateValid = (state: string, country_id: string): boolean => {
  state = state.toUpperCase()

  if (getFeatureFromCache(FEATURE_ENABLE_LRX_33701_STATE_VALIDATION)) {
    return findStateOrProvince(state, country_id) !== undefined
  } else {
    if (country_id.toUpperCase() === US_COUNTRY_CODE) {
      return americanStatesMap.some(usState => usState.code === state)
    }
    if (country_id.toUpperCase() === CANADA_COUNTRY_CODE) {
      return canadianProvincesMap.some(caProvince => caProvince.code === state)
    }
  }

  return true
}

export const getStateCode = (
  addressState: string,
  country_id: string
): string => {
  if (!getFeatureFromCache(FEATURE_ENABLE_LRX_33701_STATE_VALIDATION))
    return addressState

  return findStateOrProvince(addressState, country_id)?.code || addressState
}

export const isAllAddressFieldsEmpty = (
  addressDetails?: AddressDetails
): boolean => {
  if (isEmpty(addressDetails)) return true

  return (
    addressDetails?.address_1 === '' &&
    addressDetails?.address_2 === '' &&
    addressDetails?.city === '' &&
    addressDetails?.postcode === '' &&
    addressDetails?.state === '' &&
    addressDetails?.suburb === ''
  )
}

export const zipcodeValidation = (
  zipcode: string = '',
  country_id: string = ''
): AddressFieldValidationResult => {
  if (!zipcode) {
    return {
      ...DEFAULT_ERROR_RESULT,

      errorMessage: 'ZIP code is required',
    }
  }
  const { valid: isValid, errorMessage, _context } = validatePostCode(
    zipcode,
    country_id
  )
  return { isValid, errorMessage: errorMessage || '', _context: _context || '' }
}

export const streetAddress1Validation = (
  address_1: string = ''
): AddressFieldValidationResult => {
  if (!address_1)
    return {
      ...DEFAULT_ERROR_RESULT,
      errorMessage: 'Address is required',
    }

  if (address_1.length > MAX_ADDRESS_LENGTH)
    return {
      ...DEFAULT_ERROR_RESULT,
      errorMessage: `Address must be no longer than ${MAX_ADDRESS_LENGTH} characters`,
    }

  return DEFAULT_SUCCESS_RESULT
}

export const streetAddress2Validation = (
  address_2: string = ''
): AddressFieldValidationResult => {
  if (address_2.length > MAX_ADDRESS_LENGTH)
    return {
      ...DEFAULT_ERROR_RESULT,
      errorMessage: `Address must be no longer than ${MAX_ADDRESS_LENGTH} characters`,
    }

  return DEFAULT_SUCCESS_RESULT
}

export const cityValidation = (
  city: string = ''
): AddressFieldValidationResult => {
  if (!city)
    return {
      ...DEFAULT_ERROR_RESULT,
      errorMessage: 'City is required',
    }

  if (city.length > MAX_CITY_LENGTH)
    return {
      ...DEFAULT_ERROR_RESULT,
      errorMessage: `City name must be no longer than ${MAX_CITY_LENGTH} characters`,
    }

  return DEFAULT_SUCCESS_RESULT
}

export const stateValidation = (
  state: string = '',
  country_id: string = ''
): AddressFieldValidationResult => {
  // Require & validate states only for US/CA Addresses
  if (isStateRequired(country_id)) {
    if (!state)
      return {
        ...DEFAULT_ERROR_RESULT,
        errorMessage: 'State is required',
      }

    if (!isStateValid(state, country_id))
      return {
        ...DEFAULT_ERROR_RESULT,
        errorMessage:
          'The state name is incorrect. Please select a state from the list',
      }

    return DEFAULT_SUCCESS_RESULT
  }

  // Check only the max-length for all other countries
  if (state.length > MAX_STATE_LENGTH)
    return {
      ...DEFAULT_ERROR_RESULT,
      errorMessage: `State name must be no longer than ${MAX_STATE_LENGTH} characters`,
    }

  return DEFAULT_SUCCESS_RESULT
}

export const countryValidation = (
  country_id: string = ''
): AddressFieldValidationResult => {
  if (!country_id)
    return { ...DEFAULT_ERROR_RESULT, errorMessage: 'Country is required' }

  if (countryCodesMap[country_id] === undefined)
    return { ...DEFAULT_ERROR_RESULT, errorMessage: 'Invalid country' }

  return DEFAULT_SUCCESS_RESULT
}
